// CB:BEGIN require-admin-component
import { useUser } from "@clerk/clerk-react";
import { useEffect, useState, useRef, useCallback } from "react";
import { getMe } from "@/lib/me";

interface RequireAdminProps {
  children: JSX.Element;
  onNavigate?: (screen: string) => void;
}

export default function RequireAdmin({ children, onNavigate }: RequireAdminProps) {
  // CB:BEGIN enable-admin-guard
  const { isSignedIn, user } = useUser();
  const [allowed, setAllowed] = useState<null | boolean>(null);
  const [shouldRedirect, setShouldRedirect] = useState<{ to: string; reason: string } | null>(null);
  const hasCheckedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  const checkAdminStatus = useCallback(async (userId: string) => {
    try {
      console.log('🔐 RequireAdmin: Checking admin status for user:', userId);
      // Prefer Clerk metadata first
      try {
        const meta = (user?.publicMetadata as any) || {};
        const clerkIsAdmin = (meta.usertype || meta.userType) === 'admin';
        if (clerkIsAdmin) {
          setAllowed(true);
          return;
        }
      } catch {}
      
      // CB:BEGIN api-admin-check
      // Check admin role via /api/me endpoint
      // Only call if user is signed in
      if (!isSignedIn) {
        setShouldRedirect({ to: 'sign-in', reason: 'Not signed in' });
        return;
      }
      
      try {
        const { getApiUrl } = await import('../lib/getApiUrl');
        // CB:BEGIN get-token-for-api-call
        // Get Clerk token before making API call
        const clerk = (window as any).Clerk;
        const headers: Record<string, string> = {};

        if (clerk?.session) {
          try {
            const token = await clerk.session.getToken();
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            } else {
              setShouldRedirect({ to: 'sign-in', reason: 'No token' });
              return;
            }
          } catch (tokenError) {
            setShouldRedirect({ to: 'sign-in', reason: 'Token retrieval failed' });
            return;
          }
        } else {
          setShouldRedirect({ to: 'sign-in', reason: 'No session' });
          return;
        }
        // CB:END get-token-for-api-call
        
        const response = await fetch(getApiUrl('/api/me'), {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          const userData = await response.json();
          const isAdmin = userData?.role === 'ADMIN';
          console.log('🔐 RequireAdmin API check:', { role: userData?.role, isAdmin });
          
          if (isAdmin) {
            setAllowed(true);
            return;
          } else {
            setShouldRedirect({ to: 'landing', reason: 'Not admin' });
            return;
          }
        } else if (response.status === 401) {
          setShouldRedirect({ to: 'sign-in', reason: 'Unauthorized (sign in again)' });
          return;
        } else if (response.status === 404) {
          setShouldRedirect({ to: 'api-unavailable', reason: 'Endpoint not found (404). Ensure backend is up to date.' });
          return;
        } else if (response.status === 403) {
          // CB:BEGIN require-admin-403-split
          // Distinguish "requires legal acceptance" (-> accept-terms) from "not admin" (-> landing)
          let json: any = null;
          try { json = await response.json(); } catch { /* non-JSON body */ }
          if (json?.code === 'REQUIRES_ACCEPTANCE') {
            setShouldRedirect({ to: 'accept-terms', reason: 'Legal acceptance required' });
          } else {
            setShouldRedirect({ to: 'landing', reason: 'Access denied' });
          }
          // CB:END require-admin-403-split
          return;
        }
      } catch (error: any) {
        const isNetworkError =
          error?.message === 'Failed to fetch' || (error?.message && /fetch|network|connection/i.test(String(error.message)));
        if (!isNetworkError) console.error('❌ RequireAdmin API check failed:', error);
        setShouldRedirect({ to: isNetworkError ? 'api-unavailable' : 'sign-in', reason: isNetworkError ? 'API unavailable' : 'API check failed' });
        return;
      }
      // CB:END api-admin-check
      
      // Fallback to original getMe() approach
      const me = await getMe();
      if (!me) {
        setShouldRedirect({ to: 'sign-in', reason: 'Session missing' });
        return;
      }
      if (me.role !== "ADMIN") {
        setShouldRedirect({ to: 'landing', reason: 'Not admin' });
      } else {
        setAllowed(true);
      }
    } catch (error: any) {
      const isNetworkError =
        error?.message === 'Failed to fetch' || (error?.message && /fetch|network|connection/i.test(String(error.message)));
      if (!isNetworkError) console.error('❌ RequireAdmin: Error checking admin status:', error);
      setShouldRedirect({ to: isNetworkError ? 'api-unavailable' : 'landing', reason: isNetworkError ? 'API unavailable' : 'API error' });
    }
  }, []);

  useEffect(() => {
    // Only check once when user becomes available and hasn't been checked for this user
    if (!isSignedIn || !user) {
      if (!isSignedIn) {
        setShouldRedirect({ to: 'sign-in', reason: 'Not signed in' });
      }
      return;
    }

    // Reset state if user changed
    if (lastUserIdRef.current !== user.id) {
      hasCheckedRef.current = false;
      setAllowed(null);
      setShouldRedirect(null);
      lastUserIdRef.current = user.id;
    }

    // Only check if we haven't checked for this user yet
    if (!hasCheckedRef.current) {
      hasCheckedRef.current = true;
      checkAdminStatus(user.id);
    }
  }, [isSignedIn, user?.id, checkAdminStatus]); // CB:FIX Only depend on user ID, not the entire user object

  // Handle navigation using the app's custom system (skip for api-unavailable so we show inline panel)
  useEffect(() => {
    if (shouldRedirect && shouldRedirect.to !== 'api-unavailable') {
      if (onNavigate) {
        onNavigate(shouldRedirect.to);
      } else {
        window.history.pushState({ screen: shouldRedirect.to }, '', `/${shouldRedirect.to}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }
  }, [shouldRedirect, onNavigate]);

  // CB:BEGIN api-unavailable-panel
  if (shouldRedirect?.to === 'api-unavailable') {
    const is404 = shouldRedirect.reason?.includes('404');
    return (
      <div className="p-8 max-w-lg" data-testid="admin-api-unavailable">
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 12, padding: 24 }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 20 }}>{is404 ? 'Endpoint not found' : 'Backend unavailable'}</h2>
          <p style={{ color: '#92400e', margin: '0 0 16px 0' }}>
            {is404 ? 'The /api/me route was not found (404). Deploy or run the canonical backend.' : 'The API is unreachable (backend not running or wrong URL). Start the backend, then retry.'}
          </p>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 16px 0' }}>
            From the repo root: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>npm run dev:backend</code> then <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>npm run dev:frontend</code> (or <code>npm run dev</code> to start both).
          </p>
          <button
            type="button"
            onClick={() => {
              hasCheckedRef.current = false;
              setShouldRedirect(null);
              setAllowed(null);
              if (user?.id) checkAdminStatus(user.id);
            }}
            style={{ padding: '8px 16px', borderRadius: 8, background: '#f59e0b', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  // CB:END api-unavailable-panel

  if (shouldRedirect) {
    return <div className="p-8">Redirecting to {shouldRedirect.reason}...</div>;
  }

  if (allowed === null) {
    return <div className="p-8">Checking access…</div>;
  }

  return children;
  // CB:END enable-admin-guard
}
// CB:END require-admin-component